"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.player = exports.server = void 0;
const vec3_1 = require("vec3");
const materialToSound = {
    undefined: 'stone',
    rock: 'stone',
    dirt: 'grass',
    plant: 'grass',
    wool: 'cloth',
    web: 'cloth',
    wood: 'wood'
};
const server = (serv, { version }) => {
    const mcData = serv.mcData;
    const itemPlaceHandlers = new Map();
    serv.placeItem = (data) => {
        const handler = itemPlaceHandlers.get(data.item.type);
        return handler ? handler(data) : (serv.supportFeature('theFlattening') ? {} : { id: data.item.type, data: data.item.metadata });
    };
    /**
     * The handler is called when an item of the given type is
     * used to place a block. Arguments are the item, direction
     * and angle
     * It should return the id and data of the block to place
     */
    serv.onItemPlace = (name, handler, warn = true) => {
        const item = mcData.itemsByName[name] ?? mcData.blocksByName[name];
        if (!item) {
            serv.warn(`Unknown item or block ${name}`);
            return;
        }
        if (itemPlaceHandlers.has(item.id) && warn) {
            serv.warn(`onItemPlace handler was registered twice for ${name}`);
        }
        itemPlaceHandlers.set(item.id, handler);
    };
    if (serv.supportFeature('theFlattening')) {
        const parseValue = (value, state) => {
            if (state.type === 'enum') {
                return state.values.indexOf(value);
            }
            if (state.type === 'bool') {
                return value ? 0 : 1;
            }
            return parseInt(value, 10);
        };
        serv.setBlockDataProperties = (baseData, states, properties) => {
            let data = 0;
            let offset = 1;
            for (let i = states.length - 1; i >= 0; i--) {
                const prop = states[i];
                let value = baseData % prop.num_values;
                baseData = Math.floor(baseData / prop.num_values);
                if (properties[prop.name]) {
                    value = parseValue(properties[prop.name], prop);
                }
                data += offset * value;
                offset *= prop.num_values;
            }
            return data;
        };
        // Register default handlers for item -> block conversion
        for (const name of Object.keys(mcData.itemsByName)) {
            const block = mcData.blocksByName[name];
            if (block) {
                if (block.states && block.states.length > 0) {
                    serv.onItemPlace(name, ({ properties }) => {
                        const data = block.defaultState - block.minStateId;
                        return { id: block.id, data: serv.setBlockDataProperties(data, block.states, properties) };
                    });
                }
                else {
                    serv.onItemPlace(name, () => {
                        return { id: block.id, data: 0 };
                    });
                }
            }
        }
    }
    const blockInteractHandler = new Map();
    serv.interactWithBlock = async (data) => {
        const handler = blockInteractHandler.get(data.block.type);
        return handler ? handler(data) : false;
    };
    /**
     * The handler is called when a player interact with a block
     * of the given type. Arguments are the block and the player
     * It should return true if the block placement should be
     * cancelled.
     */
    serv.onBlockInteraction = (name, handler) => {
        const block = mcData.blocksByName[name];
        if (blockInteractHandler.has(block.id)) {
            serv.warn(`onBlockInteraction handler was registered twice for ${name}`);
        }
        blockInteractHandler.set(block.id, handler);
    };
    const PrismarineBlock = require('prismarine-block')(version);
    const { blocksArray: blocks } = serv.mcData;
    // todo use map for speed
    // doors/gates opening/closing
    const blocksWithOpenState = blocks.filter(b => ['_door', '_gate', '_trapdoor'].some(predicate => b.name.endsWith(predicate))).map((b) => b.name);
    for (const block of blocksWithOpenState) {
        serv.onBlockInteraction(block, ({ block, player }) => {
            const thisBlock = block;
            const toggleDoorState = async (pos) => {
                const block = await player.world.getBlock(pos);
                if (block?.type !== thisBlock.type)
                    return;
                const props = {
                    ...block.getProperties(),
                };
                props.open = !props.open;
                const newBlock = PrismarineBlock.fromProperties(block.type, props, block.biome.id);
                player.setBlock(block.position, newBlock.stateId);
            };
            toggleDoorState(block.position);
            if (block.getProperties().half) {
                toggleDoorState(block.position.offset(0, block.getProperties().half === 'upper' ? -1 : 1, 0));
            }
            return true;
        });
    }
    const commandBlocks = blocks.filter(b => b.name.endsWith('command_block')).map((b) => b.name);
    for (const block of commandBlocks) {
        serv.onBlockInteraction(block, ({ block, player }) => {
            const { Command } = serv.getCommandBlockData(block) ?? {};
            if (Command) {
                player.chat(Command);
            }
            else {
                player.chat('No entity data');
            }
            return true;
        });
    }
};
exports.server = server;
const player = function (player, serv, { version }) {
    const mcData = serv.mcData;
    const blocks = mcData.blocks;
    player._client.on('block_place', async ({ direction, location, cursorY } = {}) => {
        const referencePosition = new vec3_1.Vec3(location.x, location.y, location.z);
        const block = await player.world.getBlock(referencePosition);
        block.position = referencePosition;
        //@ts-ignore TODO
        block.direction = direction;
        const directionVector = block.boundingBox === 'empty' ? new vec3_1.Vec3(0, 0, 0) : directionToVector[direction];
        const placedPosition = referencePosition.plus(directionVector);
        if (!player.crouching && (await serv.interactWithBlock({ block, player }))) {
            // cancel optimistic placement
            void player.world.getBlock(placedPosition).then((block) => {
                // todo need to fix entity data?
                player._client.write('block_change', {
                    location: placedPosition,
                    type: block.type,
                    metadata: block.metadata
                });
            });
            return;
        }
        if (player.gameMode >= 2)
            return;
        const heldItem = player.inventory.slots[36 + player.heldItemSlot];
        if (!heldItem || direction === -1 || heldItem.type === -1)
            return;
        if (placedPosition.equals(player.position.floored()))
            return;
        const dx = player.position.x - (placedPosition.x + 0.5);
        const dz = player.position.z - (placedPosition.z + 0.5);
        const angle = Math.atan2(dx, -dz) * 180 / Math.PI + 180; // Convert to [0,360[
        if (serv.supportFeature('blockPlaceHasIntCursor'))
            cursorY /= 16;
        let half = cursorY > 0.5 ? 'top' : 'bottom';
        if (direction === 0)
            half = 'top';
        else if (direction === 1)
            half = 'bottom';
        const { id, data } = await serv.placeItem({
            item: heldItem,
            angle,
            direction,
            player,
            referencePosition,
            placedPosition,
            directionVector,
            properties: {
                rotation: Math.floor(angle / 22.5 + 0.5) & 0xF,
                axis: directionToAxis[direction],
                facing: directionToFacing[Math.floor(angle / 90 + 0.5) & 0x3],
                half,
                waterlogged: (await player.world.getBlock(placedPosition)).type === mcData.blocksByName.water.id
            }
        });
        if (!blocks[id])
            return;
        const sound = 'dig.' + (materialToSound[blocks[id].material ?? ''] || 'stone');
        serv.playSound(sound, player.world, placedPosition.offset(0.5, 0.5, 0.5), {
            pitch: 0.8
        });
        if (player.gameMode === 0) {
            heldItem.count--;
            if (heldItem.count === 0) {
                player.inventory.updateSlot(36 + player.heldItemSlot, null);
            }
            else {
                player.inventory.updateSlot(36 + player.heldItemSlot, heldItem);
            }
        }
        const stateId = serv.supportFeature('theFlattening') ? (blocks[id].minStateId + data) : (id << 4 | data);
        player.setBlock(placedPosition, stateId);
    });
};
exports.player = player;
const directionToVector = [new vec3_1.Vec3(0, -1, 0), new vec3_1.Vec3(0, 1, 0), new vec3_1.Vec3(0, 0, -1), new vec3_1.Vec3(0, 0, 1), new vec3_1.Vec3(-1, 0, 0), new vec3_1.Vec3(1, 0, 0)];
const directionToAxis = ['y', 'y', 'z', 'z', 'x', 'x'];
const directionToFacing = ['south', 'west', 'north', 'east',];
